import React, { useState, useEffect, useRef } from 'react';
import {
  Sparkles,
  Check,
  Headphones,
  ShieldCheck,
  ArrowRight,
  Database,
  CheckCircle2,
  Phone,
  User,
  Loader2,
  Award,
  Heart
} from 'lucide-react';
import { SpecialistAvatar } from './SpecialistAvatar';
import { TurnstileWidget, TurnstileWidgetRef } from './TurnstileWidget';
import { formatTurkishPhoneNumber, validateTurkishPhoneNumber } from '../utils/phoneUtils';
import { useSite } from '../context/SiteContext';

interface PriceCalculatorProps {
  selectedAreasFromParent?: string[];
  onFormSubmitted?: () => void;
}

export const PriceCalculator: React.FC<PriceCalculatorProps> = ({
  selectedAreasFromParent = [],
  onFormSubmitted,
}) => {
  let isFemale = false;
  try {
    const site = useSite();
    if (site) isFemale = site.isFemale;
  } catch {}

  const maleAreaOptions = [
    { name: 'Sakal Üstü, Yanak & Boyun', time: 10, tag: 'En Popüler' },
    { name: 'Ense & Kulak & Burun', time: 5, tag: 'Detay Bakım' },
    { name: 'Sırt & Omuz', time: 20, tag: 'Yüksek Talep' },
    { name: 'Göğüs & Karın', time: 15, tag: 'Sporcu Tercihi' },
    { name: 'Koltuk Altı', time: 5, tag: 'Hijyen' },
    { name: 'Tüm Kol & Eller', time: 15, tag: 'Estetik' },
    { name: 'Tüm Bacak & Ayak', time: 25, tag: 'Konfor' },
    { name: 'Özel Bölge (Genital & Kasık)', time: 15, tag: 'Özel Hijyen' },
  ];

  const femaleAreaOptions = [
    { name: 'Tüm Bacak & Ayak Üstü', time: 25, tag: 'En Popüler' },
    { name: 'Genital & Bikini Bölgesi', time: 15, tag: 'Özel Konfor' },
    { name: 'Koltuk Altı', time: 5, tag: 'Hijyen' },
    { name: 'Tüm Kol & Eller', time: 15, tag: 'Pürüzsüzlük' },
    { name: 'Yüz (Bıyık, Çene, Favori)', time: 10, tag: 'Hassas Bakım' },
    { name: 'Yarım Bacak (Alt/Üst)', time: 15, tag: 'Hızlı Seans' },
    { name: 'Göbek & Karın Çizgisi', time: 10, tag: 'Detay' },
    { name: 'Bel & Sırt Bölgesi', time: 15, tag: 'İpeksi Ten' },
  ];

  const areaOptions = isFemale ? femaleAreaOptions : maleAreaOptions;

  const defaultInitialAreas = isFemale
    ? ['Tüm Bacak & Ayak Üstü', 'Genital & Bikini Bölgesi', 'Koltuk Altı']
    : ['Sakal Üstü, Yanak & Boyun'];

  const [selectedAreas, setSelectedAreas] = useState<string[]>(
    selectedAreasFromParent.length > 0 ? selectedAreasFromParent : defaultInitialAreas
  );
  const [density, setDensity] = useState<'light' | 'medium' | 'dense'>('medium');
  const [experience, setExperience] = useState<'first_time' | 'had_before'>('first_time');
  const [targetGoal, setTargetGoal] = useState<'finish' | 'thinning'>('finish');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [customNote, setCustomNote] = useState('');
  const [hpTrap, setHpTrap] = useState(''); // Anti-bot honeypot field
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<TurnstileWidgetRef>(null);

  // Switch initial selection if site changes
  useEffect(() => {
    if (selectedAreasFromParent.length === 0) {
      setSelectedAreas(defaultInitialAreas);
    }
  }, [isFemale]);

  // Sync when parent changes or when package button clicked
  useEffect(() => {
    if (selectedAreasFromParent && selectedAreasFromParent.length > 0) {
      let resolved: string[] = [];
      selectedAreasFromParent.forEach((name) => {
        if (name.includes('Full') || name.includes('Altın 3') || name.includes('Komple')) {
          if (isFemale) {
            resolved.push('Tüm Bacak & Ayak Üstü', 'Genital & Bikini Bölgesi', 'Koltuk Altı');
          } else {
            resolved.push('Sakal Üstü, Yanak & Boyun', 'Sırt & Omuz', 'Göğüs & Karın', 'Koltuk Altı');
          }
        } else if (name.includes('Tüm Vücut')) {
          resolved.push(...areaOptions.map((a) => a.name));
        } else if (name.includes('Özel Bölge') || name.includes('Genital') || name.includes('Kasık') || name.includes('Bikini')) {
          resolved.push(isFemale ? 'Genital & Bikini Bölgesi' : 'Özel Bölge (Genital & Kasık)');
        } else if (name.includes('Bacak')) {
          resolved.push(isFemale ? 'Tüm Bacak & Ayak Üstü' : 'Tüm Bacak & Ayak');
        } else {
          resolved.push(name);
        }
      });
      setSelectedAreas(Array.from(new Set(resolved)));
    }
  }, [selectedAreasFromParent, isFemale]);

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [savedLeadId, setSavedLeadId] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState('');

  const toggleArea = (areaName: string) => {
    if (selectedAreas.includes(areaName)) {
      setSelectedAreas(selectedAreas.filter((a) => a !== areaName));
    } else {
      setSelectedAreas([...selectedAreas, areaName]);
    }
  };

  const selectAllPopular = () => {
    if (isFemale) {
      setSelectedAreas(['Tüm Bacak & Ayak Üstü', 'Genital & Bikini Bölgesi', 'Koltuk Altı']);
    } else {
      setSelectedAreas(['Sakal Üstü, Yanak & Boyun', 'Sırt & Omuz', 'Göğüs & Karın', 'Koltuk Altı']);
    }
  };

  const selectAllFullBody = () => {
    setSelectedAreas(areaOptions.map((a) => a.name));
  };

  const clearAll = () => {
    setSelectedAreas([]);
  };

  // Realistic in-motion multi-zone duration calculation (Max 60 mins for full body)
  const calculateEstimatedDuration = (selected: string[]) => {
    if (selected.length === 0) return 0;
    if (selected.length === 1) {
      const item = areaOptions.find((a) => a.name === selected[0]);
      return item ? item.time : 10;
    }

    if (selected.length >= 7) {
      return isFemale ? 55 : 60; // Complete full body
    }
    if (selected.length >= 4) {
      return 45;
    }
    if (selected.length === 3) {
      return isFemale ? 40 : 35; // Female Gold 3: Legs + Genital + Underarm = ~40 mins
    }
    if (selected.length === 2) {
      const rawSum = selected.reduce((acc, name) => {
        const item = areaOptions.find((a) => a.name === name);
        return acc + (item ? item.time : 10);
      }, 0);
      return Math.min(30, rawSum);
    }
    return 15;
  };

  const totalEstimatedTime = calculateEstimatedDuration(selectedAreas);
  
  const isPopularSelected = isFemale
    ? selectedAreas.length === 3 &&
      ['Tüm Bacak & Ayak Üstü', 'Genital & Bikini Bölgesi', 'Koltuk Altı'].every((a) =>
        selectedAreas.includes(a)
      )
    : selectedAreas.length === 4 &&
      ['Sakal Üstü, Yanak & Boyun', 'Sırt & Omuz', 'Göğüs & Karın', 'Koltuk Altı'].every((a) =>
        selectedAreas.includes(a)
      );

  const isAllSelected = selectedAreas.length === areaOptions.length;

  const estimatedSessions = isFemale
    ? targetGoal === 'thinning'
      ? '6 - 8 Seans (Batık & Çilek Bacak Tedavisi)'
      : '6 - 8 Seans (Kalıcı İpeksi Pürüzsüzlük)'
    : targetGoal === 'thinning'
      ? '4 - 5 Seans (Seyreltme)'
      : '6 - 8 Seans (Kalıcı Bitirme)';

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatTurkishPhoneNumber(e.target.value);
    setClientPhone(formatted);
    if (submitError) setSubmitError('');
  };

  // Handle Form Submission to DB
  const handleSaveAndSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedAreas.length === 0) {
      setSubmitError('Lütfen en az bir uygulama alanı seçiniz.');
      return;
    }

    const phoneValidation = validateTurkishPhoneNumber(clientPhone);
    if (!phoneValidation.isValid) {
      setSubmitError(phoneValidation.error || 'Lütfen geçerli bir telefon numarası giriniz.');
      return;
    }

    setSubmitError('');
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: clientName.slice(0, 100),
          phone: phoneValidation.normalized,
          selectedAreas,
          hairDensity: density === 'light' ? 'Hafif / İnce' : density === 'dense' ? (isFemale ? 'Sert / Batığa Yatkın' : 'Sert / Sık') : 'Orta Yoğunluk',
          skinType: 'Buğday / Normal',
          targetGoal: targetGoal === 'finish' 
            ? (isFemale ? 'Kalıcı Pürüzsüzlük (%90+)' : 'Tam Kalıcı Bitirme (%90+)')
            : (isFemale ? 'Batık & Çilek Bacak Tedavisi' : 'Doğal Seyreltme (%60-70)'),
          experience: experience === 'first_time' ? 'İlk Kez Lazer Yaptıracak' : 'Daha Önce Yaptırmış',
          estimatedDuration: `~${totalEstimatedTime} Dk`,
          notes: customNote.slice(0, 500),
          source: isFemale ? 'Kadın Bölge & Randevu Planlama Modülü' : 'Bölge & Randevu Planlama Modülü',
          hp_email: hpTrap, // Anti-bot honeypot
          turnstileToken,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitSuccess(true);
        setSavedLeadId(data.leadId);
        if (onFormSubmitted) onFormSubmitted();
      } else {
        setSubmitError(data.error || 'Talep kaydedilirken bir sorun oluştu.');
        turnstileRef.current?.reset();
        setTurnstileToken('');
      }
    } catch (err: any) {
      setSubmitError('Sunucu bağlantı hatası. Lütfen daha sonra tekrar deneyiniz.');
      turnstileRef.current?.reset();
      setTurnstileToken('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenLiveChatDirect = () => {
    if (typeof window !== 'undefined' && window.openLiveChat) {
      window.openLiveChat();
    } else if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('open-live-chat'));
    }
  };

  return (
    <section id="fiyat-hesapla" className="py-12 sm:py-16 bg-slate-50 border-t border-b border-slate-200 relative">
      {/* Background ambient light */}
      <div className={`absolute top-1/2 right-10 w-96 h-96 ${isFemale ? 'bg-rose-200/20' : 'bg-amber-200/20'} blur-[120px] rounded-full pointer-events-none`} />

      <div className="max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12 xl:px-16 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto space-y-2.5 mb-8">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold shadow-2xs ${
            isFemale ? 'bg-rose-50 border border-rose-200 text-rose-900' : 'bg-amber-50 border border-amber-300 text-amber-900'
          }`}>
            <Sparkles className={`w-3.5 h-3.5 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />
            <span>{isFemale ? 'Ücretsiz Doktor Görüşmesi & Kıl Analizi' : 'Ücretsiz Ön Görüşme & Kıl Analizi'}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            Kişiye Özel{' '}
            <span className={isFemale ? 'text-rose-600' : 'text-amber-600'}>
              Bölge & Seans Planlama
            </span>
          </h2>
          <p className="text-slate-600 text-xs sm:text-sm md:text-base leading-relaxed font-normal">
            {isFemale
              ? 'Bursa Nilüfer FSM Bulvarı kadın kliniğimizde seans planı ve avantajlı paket fiyatlandırması; kıl kökü yapınız ve ten hassasiyetiniz hekimimiz ve uzman kadın estetisyenlerimiz tarafından yüz yüze ücretsiz analiz edilerek belirlenir.'
              : 'Kliniğimizde net seans planı ve avantajlı paket fiyatlandırması; kıl kökü yapınız ve cilt tipiniz hekimimiz tarafından yüz yüze ücretsiz analiz edilerek belirlenir. İstediğiniz bölgeleri seçerek ön görüşme randevunuzu hemen oluşturabilirsiniz.'}
          </p>
        </div>

        {/* Unified Calculator & Live Appointment Cockpit */}
        <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm overflow-hidden grid grid-cols-1 lg:grid-cols-12">
          
          {/* Left Column: Interactive Preferences & Area Selection */}
          <div className="lg:col-span-7 xl:col-span-8 p-6 sm:p-8 lg:p-9 space-y-6">
            
            {/* Top Step Badge */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-black flex items-center justify-center">1</span>
                <span className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-wide">
                  Uygulama Alanı ve Tercihlerinizi Belirleyin
                </span>
              </div>
              <span className="text-[11px] font-medium text-slate-500 hidden sm:inline">
                Anında Sağ Panelde Hesaplanır
              </span>
            </div>

            {/* Step 1: Area Selection */}
            <div>
              <div className="flex items-center justify-between mb-3.5 flex-wrap gap-2">
                <label className="text-xs sm:text-sm font-bold text-slate-800 flex items-center gap-1.5">
                  <span>Lazer Yapılmasını İstediğiniz Bölgeler:</span>
                </label>
                <div className="flex items-center gap-1.5 text-xs flex-wrap">
                  <button
                    type="button"
                    onClick={selectAllPopular}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      isPopularSelected
                        ? isFemale ? 'bg-rose-600 text-white shadow-2xs' : 'bg-amber-600 text-white shadow-2xs'
                        : isFemale ? 'bg-rose-50 text-rose-900 hover:bg-rose-100 border border-rose-200' : 'bg-amber-50 text-amber-900 hover:bg-amber-100 border border-amber-300'
                    }`}
                    title={isFemale ? 'Tüm Bacak + Genital + Koltuk Altı (~40 Dk)' : 'Sırt + Göğüs + Koltuk Altı + Sakal (~45 dk)'}
                  >
                    {isFemale ? '⭐ Altın 3’lü Paket (~40 Dk)' : '⭐ Popüler 4’lü (~45 Dk)'}
                  </button>
                  <button
                    type="button"
                    onClick={selectAllFullBody}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      isAllSelected
                        ? 'bg-slate-900 text-white shadow-2xs'
                        : 'bg-slate-100 text-slate-800 hover:bg-slate-200 border border-slate-300'
                    }`}
                    title="Tepeden Tırnağa Tüm Bölgeler"
                  >
                    ⚡ Full Vücut ({isFemale ? '~55 Dk' : '~60 Dk'})
                  </button>
                  <span className="text-slate-300 hidden sm:inline">|</span>
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-slate-500 hover:text-slate-800 font-medium px-1.5 py-1 hover:underline cursor-pointer text-xs"
                  >
                    Temizle
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {areaOptions.map((area) => {
                  const isSelected = selectedAreas.includes(area.name);
                  return (
                    <button
                      key={area.name}
                      type="button"
                      onClick={() => toggleArea(area.name)}
                      className={`p-3 rounded-2xl border text-left flex items-center justify-between transition-all cursor-pointer ${
                        isSelected
                          ? isFemale
                            ? 'bg-rose-50/80 border-rose-500 text-slate-900 font-bold shadow-2xs'
                            : 'bg-amber-50/80 border-amber-500 text-slate-900 font-bold shadow-2xs'
                          : 'bg-slate-50/80 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`w-5 h-5 rounded-lg flex items-center justify-center border transition-colors ${
                            isSelected
                              ? isFemale
                                ? 'bg-rose-600 border-rose-600 text-white'
                                : 'bg-amber-500 border-amber-500 text-slate-950'
                              : 'bg-white border-slate-300 text-transparent'
                          }`}
                        >
                          <Check className="w-3.5 h-3.5 stroke-[3]" />
                        </div>
                        <span className="text-xs sm:text-sm">{area.name}</span>
                      </div>
                      <span className="text-[10px] font-semibold text-slate-600 bg-white px-2 py-0.5 rounded-lg border border-slate-200">
                        {area.time} dk
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2: Goal & Hair Density */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-4 border-t border-slate-100">
              {/* Target Goal */}
              <div>
                <label className="text-xs sm:text-sm font-bold text-slate-800 block mb-2.5">
                  Hedeflenen Sonuç:
                </label>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setTargetGoal('finish')}
                    className={`p-2.5 sm:p-3 rounded-xl border text-center font-semibold transition-all cursor-pointer ${
                      targetGoal === 'finish'
                        ? isFemale
                          ? 'bg-rose-50 border-rose-500 text-rose-900 font-bold shadow-2xs'
                          : 'bg-amber-50 border-amber-500 text-amber-900 font-bold shadow-2xs'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    {isFemale ? 'Kalıcı Pürüzsüzlük (%90+)' : 'Tam Kalıcı Bitirme (%90+)'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setTargetGoal('thinning')}
                    className={`p-2.5 sm:p-3 rounded-xl border text-center font-semibold transition-all cursor-pointer ${
                      targetGoal === 'thinning'
                        ? isFemale
                          ? 'bg-rose-50 border-rose-500 text-rose-900 font-bold shadow-2xs'
                          : 'bg-amber-50 border-amber-500 text-amber-900 font-bold shadow-2xs'
                        : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    {isFemale ? 'Batık & Çilek Bacak Tedavisi' : 'Doğal Seyreltme (%60-70)'}
                  </button>
                </div>
              </div>

              {/* Hair Density */}
              <div>
                <label className="text-xs sm:text-sm font-bold text-slate-800 block mb-2.5">
                  {isFemale ? 'Kıl Yapısı & Cilt Hassasiyeti:' : 'Kıl Kökü Yoğunluğu:'}
                </label>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {[
                    { id: 'light', label: isFemale ? 'İnce Ayva Tüyü' : 'Hafif / İnce' },
                    { id: 'medium', label: 'Orta Yoğun' },
                    { id: 'dense', label: isFemale ? 'Sert / Batığa Yatkın' : 'Sert / Sık' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setDensity(item.id as any)}
                      className={`p-2.5 rounded-xl border text-center font-semibold transition-all cursor-pointer ${
                        density === item.id
                          ? isFemale
                            ? 'bg-rose-50 border-rose-500 text-rose-900 font-bold shadow-2xs'
                            : 'bg-amber-50 border-amber-500 text-amber-900 font-bold shadow-2xs'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Step 3: Prior Experience */}
            <div className="pt-4 border-t border-slate-100">
              <label className="text-xs font-bold text-slate-800 block mb-2">
                Daha Önce Lazer Epilasyon Yaptırdınız mı?
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs">
                <button
                  type="button"
                  onClick={() => setExperience('first_time')}
                  className={`py-2.5 px-3 rounded-xl border text-center font-semibold transition-all cursor-pointer ${
                    experience === 'first_time'
                      ? 'bg-slate-900 border-slate-900 text-white'
                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {isFemale ? 'İlk Defa Yaptıracağım (Hassas Cilt & Kıl Analizi)' : 'İlk Defa Yaptıracağım (Kıl Analizi İstiyorum)'}
                </button>
                <button
                  type="button"
                  onClick={() => setExperience('had_before')}
                  className={`py-2.5 px-3 rounded-xl border text-center font-semibold transition-all cursor-pointer ${
                    experience === 'had_before'
                      ? 'bg-slate-900 border-slate-900 text-white'
                      : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {isFemale ? 'Daha Önce Başka Yerde/Farklı Cihazla Yaptırdım' : 'Daha Önce Başka Yerde Yaptırdım'}
                </button>
              </div>
            </div>

          </div>

          {/* Right Column: Seamless Integrated Live Cockpit & Form */}
          <div className="lg:col-span-5 xl:col-span-4 bg-slate-50/70 border-t lg:border-t-0 lg:border-l border-slate-200/90 p-6 sm:p-8 space-y-5 flex flex-col justify-between">
            
            <div className="space-y-4">
              {/* Header with Live Sync Indicator */}
              <div className="border-b border-slate-200 pb-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full text-white text-xs font-black flex items-center justify-center ${
                      isFemale ? 'bg-rose-600' : 'bg-amber-600'
                    }`}>2</span>
                    <span className={`text-xs font-bold uppercase tracking-wider ${
                      isFemale ? 'text-rose-800' : 'text-amber-800'
                    }`}>
                      Canlı Seans Planı
                    </span>
                  </div>
                  <span className="text-[10px] text-emerald-800 bg-emerald-100/70 border border-emerald-300/60 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" /> Canlı Senkron
                  </span>
                </div>
                <h3 className="text-lg sm:text-xl font-black text-slate-900">Ön Görüşme & Randevu Özeti</h3>
              </div>

              {/* Selected Areas Summary Pill List */}
              <div>
                <span className="text-xs font-semibold text-slate-600 block mb-1.5">
                  Seçilen Bölgeler ({selectedAreas.length}):
                </span>
                {selectedAreas.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pr-1">
                    {selectedAreas.map((area) => (
                      <span
                        key={area}
                        className="text-xs bg-white text-slate-800 font-medium px-2.5 py-1 rounded-lg border border-slate-200/90 shadow-2xs flex items-center gap-1.5"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isFemale ? 'bg-rose-500' : 'bg-amber-500'}`} />
                        {area}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className={`text-xs font-medium p-2.5 rounded-xl border ${
                    isFemale ? 'text-rose-800 bg-rose-50 border-rose-200' : 'text-amber-800 bg-amber-50 border-amber-200'
                  }`}>
                    Lütfen sol taraftan en az 1 bölge seçiniz.
                  </div>
                )}
              </div>

              {/* Parameters Breakdown */}
              <div className="bg-white p-3.5 rounded-2xl border border-slate-200/80 shadow-2xs space-y-2 text-xs">
                <div className="flex justify-between text-slate-600 items-center">
                  <span>Tahmini Seans Süresi:</span>
                  <div className="text-right">
                    <strong className="text-slate-900 font-bold text-sm">~{totalEstimatedTime} Dakika</strong>
                    {selectedAreas.length >= (isFemale ? 3 : 4) && (
                      <span className="block text-[10px] text-emerald-700 font-semibold">
                        {isFemale ? '(Safir Buz Lazer Hızlı Tarama)' : '(Buz Lazer Hızlı Tarama Paketi)'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Öngörülen Seans Sayısı:</span>
                  <strong className="text-slate-900 font-bold">{estimatedSessions}</strong>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Soğutma & Konfor:</span>
                  <strong className={isFemale ? 'text-rose-700 font-bold' : 'text-amber-700 font-bold'}>
                    -5°C Safir Buz Başlık (Acısız)
                  </strong>
                </div>
                {isFemale && (
                  <div className="flex justify-between text-slate-600">
                    <span>Uygulama Ekibi:</span>
                    <strong className="text-slate-900 font-bold">Kadın Estetisyen & %100 Mahremiyet</strong>
                  </div>
                )}
              </div>

              {/* Clinical Transparency Notice */}
              <div className={`p-3 rounded-xl text-xs space-y-1 border ${
                isFemale ? 'bg-rose-50/70 border-rose-200/80 text-slate-700' : 'bg-amber-50/70 border-amber-200/80 text-slate-700'
              }`}>
                <div className={`font-bold flex items-center gap-1.5 ${isFemale ? 'text-rose-950' : 'text-amber-950'}`}>
                  <Award className={`w-3.5 h-3.5 shrink-0 ${isFemale ? 'text-rose-600' : 'text-amber-600'}`} />
                  <span>Yüz Yüze Ücretsiz Muayene & Fiyat Politikamız</span>
                </div>
                <p className="text-[11px] leading-relaxed text-slate-600">
                  {isFemale
                    ? 'Kliniğimizde ezbere sabit fiyat yerine; ten renginiz, kıl inceliğiniz ve batık durumunuz hekimimiz tarafından ücretsiz analiz edilip bütçenize en uygun avantajlı paket yüz yüze sunulur.'
                    : 'Kliniğimizde ezbere sabit paket yerine; kıl kökü yapınız ve ten renginiz hekim kontrolünde değerlendirilip size en uygun avantajlı paket yüz yüze sunulur. Gizli veya sürpriz ek ücret çıkarılmaz.'}
                </p>
              </div>
            </div>

            {/* Form Section */}
            {submitSuccess ? (
              <div className="p-5 bg-emerald-50 border border-emerald-200 rounded-2xl space-y-3 text-center animate-in fade-in duration-300">
                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 mx-auto flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h4 className="text-sm font-bold text-emerald-950">Ön Görüşme Talebiniz Alındı!</h4>
                <p className="text-xs text-emerald-900">
                  Kayıt No: <strong className={isFemale ? 'text-rose-800' : 'text-amber-800'}>#{savedLeadId}</strong>. FSM Bulvarı kliniğimizden uzmanımız en kısa sürede randevu teyidi için size ulaşacaktır.
                </p>
                
                {/* Secondary Option: Direct Live Chat */}
                <button
                  type="button"
                  onClick={handleOpenLiveChatDirect}
                  className={`w-full py-2.5 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-2 shadow transition-all mt-2 cursor-pointer border ${
                    isFemale ? 'border-rose-500/50' : 'border-amber-500/50'
                  }`}
                >
                  <SpecialistAvatar size="xs" showOnlineStatus={true} bordered={false} />
                  <span>Uzmanla Canlı Destek Başlat</span>
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSaveAndSubmit}
                className="space-y-3 pt-1"
                toolname="calculate_laser_price_and_book"
                tooldescription={isFemale ? "Bursa kadın lazer epilasyon kişiye özel seans ve fiyat hesaplama randevu formu" : "Bursa erkek lazer epilasyon kişiye özel seans ve fiyat hesaplama randevu formu"}
              >
                
                {/* Anti-Bot Honeypot Field */}
                <div className="hidden" aria-hidden="true" style={{ display: 'none' }}>
                  <input
                    type="text"
                    name="website_url_hp"
                    toolparamdescription="Güvenlik kontrolü"
                    tabIndex={-1}
                    autoComplete="off"
                    value={hpTrap}
                    onChange={(e) => setHpTrap(e.target.value)}
                  />
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="calc_client_name" className="sr-only">Adınız Soyadınız</label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      id="calc_client_name"
                      type="text"
                      name="client_name"
                      aria-label="Adınız Soyadınız"
                      toolparamdescription="Danışanın adı ve soyadı"
                      placeholder="Adınız Soyadınız"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl pl-9 pr-3 py-2.5 text-xs sm:text-sm text-slate-900 placeholder:text-slate-500 focus:bg-white focus:outline-none focus:border-rose-500 transition-colors"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="calc_client_phone" className="sr-only">Telefon Numaranız</label>
                  <div className="relative">
                    <Phone className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      id="calc_client_phone"
                      type="tel"
                      name="client_phone"
                      aria-label="Telefon Numaranız"
                      toolparamdescription="Danışanın telefon / WhatsApp iletişim numarası"
                      inputMode="tel"
                      autoComplete="tel"
                      maxLength={14}
                      required
                      placeholder="05XX XXX XX XX"
                      value={clientPhone}
                      onChange={handlePhoneChange}
                      className="w-full bg-slate-50 border border-slate-300 rounded-xl pl-9 pr-3 py-2.5 text-xs sm:text-sm text-slate-900 placeholder:text-slate-500 focus:bg-white focus:outline-none focus:border-rose-500 transition-colors font-medium tracking-wide"
                    />
                  </div>
                </div>

                {/* Note / Question */}
                <div>
                  <label htmlFor="calc_custom_note" className="sr-only">Eklemek istediğiniz not veya batık sorununuz</label>
                  <input
                    id="calc_custom_note"
                    type="text"
                    name="custom_note"
                    aria-label="Eklemek istediğiniz not veya batık sorununuz"
                    toolparamdescription="Danışanın batık, çilek bacak veya özel bölge notları"
                    placeholder={isFemale ? "Eklemek istediğiniz not, batık/çilek bacak veya seans talebiniz..." : "Eklemek istediğiniz not veya batık sorununuz..."}
                    value={customNote}
                    onChange={(e) => setCustomNote(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-900 placeholder:text-slate-500 focus:bg-white focus:outline-none focus:border-rose-500 transition-colors"
                  />
                </div>

                {/* Turnstile Spam Protection */}
                <TurnstileWidget
                  ref={turnstileRef}
                  onVerify={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken('')}
                />

                {submitError && (
                  <p className="text-xs text-rose-700 bg-rose-50 p-2.5 rounded-xl border border-rose-200">
                    {submitError}
                  </p>
                )}

                {/* Primary DB Save & Appointment Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting || selectedAreas.length === 0}
                  className="w-full py-3.5 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md shadow-slate-900/20 border border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.01] cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className={`w-4 h-4 animate-spin ${isFemale ? 'text-rose-400' : 'text-amber-400'}`} />
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className={`w-4 h-4 ${isFemale ? 'text-rose-400 fill-rose-400' : 'text-amber-400 fill-amber-400'}`} />
                      <span>Ücretsiz Ön Görüşme Randevusu Al</span>
                      <ArrowRight className="w-4 h-4 text-slate-300" />
                    </>
                  )}
                </button>

                {/* Direct Live Chat Alternative Button */}
                <button
                  type="button"
                  onClick={handleOpenLiveChatDirect}
                  disabled={selectedAreas.length === 0}
                  className="w-full py-2.5 px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 font-bold text-xs flex items-center justify-center gap-2 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <SpecialistAvatar size="xs" showOnlineStatus={true} bordered={false} />
                  <span>Uzmanımızla Canlı Destek Üzerinden Görüş</span>
                </button>

              </form>
            )}

            <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 pt-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span>Talebiniz gizlilik protokolü ile doğrudan hekim asistanımıza iletilir.</span>
            </div>

          </div>

        </div>

      </div>
    </section>
  );
};
